import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../services/api';

// OpenStreetMap via Leaflet CDN
const LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
const LEAFLET_JS  = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';

function loadLeaflet() {
  return new Promise((resolve) => {
    if (window.L) { resolve(window.L); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = LEAFLET_CSS;
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
  });
}

export default function FlottaPage() {
  const mapRef       = useRef(null);
  const leafletMap   = useRef(null);
  const markersRef   = useRef({});
  const polylineRef  = useRef(null);

  const [dispositivi, setDispositivi] = useState([]);
  const [posizioni,   setPosizioni]   = useState({});
  const [selDev,      setSelDev]      = useState(null);
  const [vista,       setVista]       = useState('realtime'); // realtime | storico
  const [storRange,   setStorRange]   = useState({ from: today(-7), to: today(0) });
  const [storRoute,   setStorRoute]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [errore,      setErrore]      = useState('');

  function today(offsetDays = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString().substring(0, 10);
  }

  // Init mappa
  useEffect(() => {
    loadLeaflet().then(L => {
      if (leafletMap.current || !mapRef.current) return;
      leafletMap.current = L.map(mapRef.current).setView([41.9, 12.5], 6);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(leafletMap.current);
    });
    return () => {
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
  }, []);

  // Carica dispositivi
  useEffect(() => {
    api.get('/flotta/dispositivi').then(r => {
      const data = r.data;
      setDispositivi(Array.isArray(data) ? data : (data?.data || data?.devices || []));
    }).catch(e => setErrore('Errore dispositivi: ' + e.message));
  }, []);

  // Poll posizioni ogni 10 secondi
  const aggiornaPosizioni = useCallback(async () => {
    if (vista !== 'realtime') return;
    try {
      const r = await api.get('/flotta/posizioni');
      const rawPos = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      const pos = {};
      rawPos.forEach(p => { pos[p.deviceId] = p; });
      setPosizioni(pos);
    } catch(e) { /* silenzioso */ }
  }, [vista]);

  useEffect(() => {
    aggiornaPosizioni();
    const interval = setInterval(aggiornaPosizioni, 10000);
    return () => clearInterval(interval);
  }, [aggiornaPosizioni]);

  // Aggiorna marker sulla mappa
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletMap.current) return;

    dispositivi.forEach(dev => {
      const pos = posizioni[dev.id];
      if (!pos) return;

      const lat = pos.latitude, lng = pos.longitude;
      if (!lat && !lng) return;

      const speed  = pos.speed ? Math.round(pos.speed * 1.852) + ' km/h' : 'Fermo';
      const popup  = `<b>${dev.name}</b><br>${speed}<br>${new Date(pos.fixTime).toLocaleString('it-IT')}`;

      if (markersRef.current[dev.id]) {
        markersRef.current[dev.id].setLatLng([lat, lng]).setPopupContent(popup);
      } else {
        const icon = L.divIcon({
          className: '',
          html: `<div style="background:#b30000;color:white;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:18px;box-shadow:0 2px 6px rgba(0,0,0,0.3)">🚐</div>`,
          iconSize: [32, 32], iconAnchor: [16, 16],
        });
        markersRef.current[dev.id] = L.marker([lat, lng], { icon })
          .addTo(leafletMap.current)
          .bindPopup(popup);
      }
    });
  }, [posizioni, dispositivi]);

  // Carica storico
  const caricaStorico = async () => {
    if (!selDev) return;
    setLoading(true); setErrore('');
    try {
      const from = storRange.from + 'T00:00:00Z';
      const to   = storRange.to   + 'T23:59:59Z';
      const r    = await api.get(`/flotta/storico?deviceId=${selDev}&from=${from}&to=${to}`);
      const route = r.data || [];
      setStorRoute(route);

      // Disegna percorso sulla mappa
      const L = window.L;
      if (!L || !leafletMap.current) return;
      if (polylineRef.current) polylineRef.current.remove();
      if (route.length === 0) { setErrore('Nessun percorso trovato per il periodo selezionato.'); return; }

      const latlngs = route.map(p => [p.latitude, p.longitude]);
      polylineRef.current = L.polyline(latlngs, { color:'#b30000', weight:3, opacity:0.8 })
        .addTo(leafletMap.current);
      leafletMap.current.fitBounds(polylineRef.current.getBounds(), { padding:[20,20] });

      // Marker inizio/fine
      L.marker(latlngs[0], {
        icon: L.divIcon({ html:'<div style="background:#16a34a;color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px">A</div>', iconSize:[24,24], iconAnchor:[12,12] })
      }).addTo(leafletMap.current).bindPopup('Inizio percorso');
      L.marker(latlngs[latlngs.length-1], {
        icon: L.divIcon({ html:'<div style="background:#b30000;color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px">B</div>', iconSize:[24,24], iconAnchor:[12,12] })
      }).addTo(leafletMap.current).bindPopup('Fine percorso');

    } catch(e) { setErrore('Errore storico: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  };

  const centraSuVeicolo = (devId) => {
    const pos = posizioni[devId];
    if (!pos || !leafletMap.current) return;
    leafletMap.current.setView([pos.latitude, pos.longitude], 14);
    if (markersRef.current[devId]) markersRef.current[devId].openPopup();
  };

  const fmtSpeed = (pos) => pos?.speed ? Math.round(pos.speed * 1.852) + ' km/h' : 'Fermo';
  const fmtTime  = (pos) => pos?.fixTime ? new Date(pos.fixTime).toLocaleString('it-IT') : '—';

  return (
    <div className="page-content" style={{ padding:0, display:'grid', gridTemplateRows:'auto 1fr', height:'calc(100vh - 60px)' }}>

      {/* HEADER */}
      <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb', background:'#fff', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
        <h2 style={{ margin:0, fontSize:16 }}>🚐 Flotta veicoli</h2>

        {/* Toggle vista */}
        <div style={{ display:'flex', gap:4 }}>
          <button className={`btn btn-sm ${vista==='realtime'?'btn-primary':'btn-secondary'}`}
            onClick={() => { setVista('realtime'); if(polylineRef.current){ polylineRef.current.remove(); polylineRef.current=null; } setStorRoute([]); }}>
            📍 Tempo reale
          </button>
          <button className={`btn btn-sm ${vista==='storico'?'btn-primary':'btn-secondary'}`}
            onClick={() => setVista('storico')}>
            📅 Storico percorsi
          </button>
        </div>

        {/* Controlli storico */}
        {vista === 'storico' && (
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <select className="form-control" style={{ width:180, fontSize:12, padding:'4px 6px' }}
              value={selDev||''} onChange={e=>setSelDev(parseInt(e.target.value))}>
              <option value="">Seleziona veicolo</option>
              {dispositivi.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <input type="date" className="form-control" style={{ width:140, fontSize:12, padding:'4px 6px' }}
              value={storRange.from} onChange={e=>setStorRange(r=>({...r,from:e.target.value}))} />
            <span style={{ fontSize:12, color:'#6b7280' }}>→</span>
            <input type="date" className="form-control" style={{ width:140, fontSize:12, padding:'4px 6px' }}
              value={storRange.to} onChange={e=>setStorRange(r=>({...r,to:e.target.value}))} />
            <button className="btn btn-primary btn-sm" onClick={caricaStorico} disabled={loading||!selDev}>
              {loading ? '...' : '🔍 Mostra percorso'}
            </button>
            {storRoute.length > 0 && (
              <span style={{ fontSize:12, color:'#6b7280' }}>{storRoute.length} punti GPS</span>
            )}
          </div>
        )}

        {errore && <span style={{ fontSize:12, color:'#dc2626' }}>⚠️ {errore}</span>}
      </div>

      {/* CONTENUTO: mappa + lista */}
      <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', overflow:'hidden' }}>

        {/* LISTA VEICOLI */}
        <div style={{ borderRight:'1px solid #e5e7eb', overflowY:'auto', background:'#f9fafb' }}>
          <div style={{ padding:'8px 12px', fontSize:11, color:'#9ca3af', fontWeight:600, borderBottom:'1px solid #e5e7eb' }}>
            {dispositivi.length} VEICOLI
          </div>
          {dispositivi.length === 0 && (
            <div style={{ padding:16, fontSize:13, color:'#9ca3af', textAlign:'center' }}>
              Nessun veicolo configurato.<br/>
              <span style={{ fontSize:11 }}>Aggiungi dispositivi su Traccar.</span>
            </div>
          )}
          {dispositivi.map(dev => {
            const pos   = posizioni[dev.id];
            const speed = fmtSpeed(pos);
            const time  = fmtTime(pos);
            const online = pos && (Date.now() - new Date(pos.deviceTime).getTime()) < 300000; // 5 min
            return (
              <div key={dev.id}
                onClick={() => { setSelDev(dev.id); centraSuVeicolo(dev.id); }}
                style={{
                  padding:'10px 12px', borderBottom:'1px solid #e5e7eb',
                  cursor:'pointer', background: selDev===dev.id ? '#fff7ed' : '#fff',
                  borderLeft: selDev===dev.id ? '3px solid #b30000' : '3px solid transparent',
                }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                  <span style={{ fontWeight:600, fontSize:13 }}>🚐 {dev.name}</span>
                  <span style={{
                    fontSize:10, padding:'1px 6px', borderRadius:10,
                    background: online ? '#dcfce7' : '#f3f4f6',
                    color: online ? '#16a34a' : '#9ca3af',
                  }}>{online ? '● Online' : '○ Offline'}</span>
                </div>
                <div style={{ fontSize:11, color:'#6b7280' }}>
                  <div>🏎 {speed}</div>
                  <div>🕐 {time}</div>
                  {dev.model && <div>🚗 {dev.model}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* MAPPA */}
        <div ref={mapRef} style={{ width:'100%', height:'100%', minHeight:400 }} />
      </div>
    </div>
  );
}
